import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Target,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  ShoppingBag,
  Flame,
  Snowflake,
  ChevronRight,
  Search as SearchIcon,
  RefreshCw,
  CheckCircle2,
} from 'lucide-react';
import { apiClient, type TrafficCreative } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { cn, formatBRL, formatPercent, formatRelative } from '../lib/utils.js';

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function previousMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

function nextMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString().slice(0, 7);
}

function roasBadge(roas: number) {
  if (roas >= 2)
    return { label: 'Vencedor', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', icon: Flame };
  if (roas >= 1)
    return { label: 'Breakeven', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: TrendingUp };
  if (roas > 0)
    return { label: 'Negativo', color: 'bg-rose-500/15 text-rose-300 border-rose-500/30', icon: TrendingDown };
  return { label: 'Sem venda', color: 'bg-slate-500/15 text-slate-400 border-slate-500/30', icon: Snowflake };
}

function CreativeRow({ c }: { c: TrafficCreative }): JSX.Element {
  const spend = Number(c.spendCents);
  const gross = Number(c.grossCents);
  const roas = spend > 0 ? gross / spend : 0;
  const badge = roasBadge(roas);
  const Icon = badge.icon;
  return (
    <tr className="hover:bg-muted/30">
      <td className="px-3 py-2.5">
        <Link
          to={`/trafego/campanha/${c.campaign}`}
          className="text-xs font-mono text-muted-foreground hover:text-primary"
        >
          {c.campaign}
        </Link>
      </td>
      <td className="px-3 py-2.5">
        <span className="font-mono text-sm font-medium">{c.creative}</span>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">{formatBRL(spend)}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{c.contacts}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{c.orders}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{formatBRL(gross)}</td>
      <td className="px-3 py-2.5 text-right">
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border',
            badge.color,
          )}
        >
          <Icon className="h-3 w-3" />
          {roas.toFixed(2)}x
        </span>
      </td>
    </tr>
  );
}

export function Trafego(): JSX.Element {
  const qc = useQueryClient();
  const [month, setMonth] = useState<string>(currentMonth());
  const [creativeSearch, setCreativeSearch] = useState('');
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['traffic-overview', month],
    queryFn: () => apiClient.trafficOverview(month),
  });

  const lastSyncQ = useQuery({
    queryKey: ['traffic-last-sync'],
    queryFn: () => apiClient.trafficLastSync(),
  });

  const syncMutation = useMutation({
    mutationFn: () => apiClient.syncMeta(),
    onSuccess: (r) => {
      setSyncResult(
        `✓ ${r.insertedAds} insights · ${r.uniqueCampaigns} campanhas · R$ ${(r.totalSpendCents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (${r.durationMs}ms)`,
      );
      qc.invalidateQueries({ queryKey: ['traffic-overview'] });
      qc.invalidateQueries({ queryKey: ['traffic-last-sync'] });
      setTimeout(() => setSyncResult(null), 8000);
    },
    onError: (err) => setSyncResult(`✗ ${String(err)}`),
  });

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Carregando tráfego...</div>;
  }

  if (isError || !data) {
    return (
      <div className="p-8 text-rose-400">Erro ao carregar tráfego: {String(error)}</div>
    );
  }

  const { kpis, campaigns, creatives, sources } = data;
  const filteredCreatives = creatives.filter((c) =>
    `${c.campaign}/${c.creative}`.toLowerCase().includes(creativeSearch.toLowerCase()),
  );
  const maxCampaignSpend = campaigns.length
    ? Math.max(...campaigns.map((c) => Number(c.spendCents)))
    : 0;

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            Tráfego & Attribution
          </h1>
          <p className="text-sm text-muted-foreground">
            Meta Ads × Ticto · ROAS por criativo · Multi-touch attribution
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary/15 text-primary hover:bg-primary/25 px-3 py-1.5 text-sm font-medium border border-primary/30 disabled:opacity-50"
            title="Puxa gasto real da Meta Graph API"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', syncMutation.isPending && 'animate-spin')}
            />
            {syncMutation.isPending ? 'Sincronizando...' : 'Sync Meta Ads'}
          </button>

          <div className="flex items-center gap-2 bg-muted/40 rounded-md px-1 py-1 border border-border">
            <button
              onClick={() => setMonth(previousMonth(month))}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
            >
              ←
            </button>
            <div className="px-3 py-1 text-sm font-medium tabular-nums">{month}</div>
            <button
              onClick={() => setMonth(nextMonth(month))}
              disabled={month >= currentMonth()}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30"
            >
              →
            </button>
          </div>
        </div>
      </div>

      {/* Sync banner */}
      {syncResult && (
        <div
          className={cn(
            'rounded-md px-4 py-2 text-sm flex items-center gap-2',
            syncResult.startsWith('✓')
              ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
              : 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
          )}
        >
          {syncResult.startsWith('✓') ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {syncResult}
        </div>
      )}

      {/* Last sync status */}
      {lastSyncQ.data && lastSyncQ.data.totalEvents > 0 && (
        <div className="text-[10px] text-muted-foreground">
          <strong>Dados reais Meta:</strong> {lastSyncQ.data.totalEvents} cost events · última
          sync {formatRelative(lastSyncQ.data.lastSyncedAt!)} · dados até{' '}
          {lastSyncQ.data.lastDataDate?.slice(0, 10)}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Ad Spend</span>
              <DollarSign className="h-4 w-4 text-rose-400" />
            </div>
            <div className="text-xl font-bold tabular-nums">{formatBRL(kpis.spendCents)}</div>
            <div className="text-xs text-muted-foreground mt-1">Meta Ads · {month}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Receita Atribuída</span>
              <ShoppingBag className="h-4 w-4 text-emerald-400" />
            </div>
            <div className="text-xl font-bold tabular-nums">{formatBRL(kpis.grossCents)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {kpis.approvedOrders} pedidos · last-touch UTM
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">ROAS</span>
              <TrendingUp
                className={cn(
                  'h-4 w-4',
                  kpis.roas >= 1 ? 'text-emerald-400' : 'text-rose-400',
                )}
              />
            </div>
            <div
              className={cn(
                'text-xl font-bold tabular-nums',
                kpis.roas >= 1 ? 'text-emerald-400' : 'text-rose-400',
              )}
            >
              {kpis.roas.toFixed(2)}x
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Margem {formatPercent(kpis.margin)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">CPA Médio</span>
              <Users className="h-4 w-4 text-blue-400" />
            </div>
            <div className="text-xl font-bold tabular-nums">{formatBRL(kpis.cpaCents)}</div>
            <div className="text-xs text-muted-foreground mt-1">por pedido aprovado</div>
          </CardContent>
        </Card>
      </div>

      {/* Campanhas + Fontes */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Campanhas</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Campanha</th>
                    <th className="px-3 py-2 font-medium text-right">Spend</th>
                    <th className="px-3 py-2 font-medium text-right">Criativos</th>
                    <th className="px-3 py-2 font-medium text-right">Contatos</th>
                    <th className="px-3 py-2 font-medium text-right">Pedidos</th>
                    <th className="px-3 py-2 font-medium text-right">Receita</th>
                    <th className="px-3 py-2 font-medium text-right">ROAS</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {campaigns.map((c) => {
                    const spend = Number(c.spendCents);
                    const gross = Number(c.grossCents);
                    const roas = spend > 0 ? gross / spend : 0;
                    const badge = roasBadge(roas);
                    const Icon = badge.icon;
                    const pct = maxCampaignSpend ? (spend / maxCampaignSpend) * 100 : 0;
                    return (
                      <tr key={c.campaign} className="hover:bg-muted/30">
                        <td className="px-3 py-3">
                          <Link
                            to={`/trafego/campanha/${c.campaign}`}
                            className="block hover:text-primary"
                          >
                            {c.campaignName ? (
                              <>
                                <div className="text-sm font-medium truncate max-w-[280px]">
                                  {c.campaignName}
                                </div>
                                <div className="text-[10px] text-muted-foreground font-mono">
                                  {c.campaign}
                                </div>
                              </>
                            ) : (
                              <span className="font-mono text-sm font-medium">
                                {c.campaign}
                              </span>
                            )}
                          </Link>
                          <div className="h-1 rounded-full bg-muted/40 overflow-hidden mt-1 max-w-[120px]">
                            <div
                              className="h-full bg-rose-500/60"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">
                          {formatBRL(spend)}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                          {c.creatives}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">{c.contacts}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{c.orders}</td>
                        <td className="px-3 py-3 text-right tabular-nums">
                          {formatBRL(gross)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border',
                              badge.color,
                            )}
                          >
                            <Icon className="h-3 w-3" />
                            {roas.toFixed(2)}x
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <Link
                            to={`/trafego/campanha/${c.campaign}`}
                            className="text-muted-foreground hover:text-primary"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                  {campaigns.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                        Sem campanhas neste mês.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Por Fonte (UTM source)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sources.map((s) => {
              const gross = Number(s.grossCents);
              const maxGross = Math.max(...sources.map((x) => Number(x.grossCents)), 1);
              return (
                <div key={s.source}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-mono font-medium">{s.source}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {s.contacts} contatos · {s.orders} pedidos
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-muted/40 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500/70"
                        style={{ width: `${(gross / maxGross) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs tabular-nums w-20 text-right">
                      {formatBRL(gross)}
                    </span>
                  </div>
                </div>
              );
            })}
            {sources.length === 0 && (
              <div className="text-sm text-muted-foreground">Sem fontes.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ranking criativos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Ranking de Criativos</CardTitle>
            <div className="relative w-64">
              <SearchIcon className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
              <input
                placeholder="Filtrar criativo..."
                value={creativeSearch}
                onChange={(e) => setCreativeSearch(e.target.value)}
                className="w-full h-8 pl-8 pr-3 text-sm rounded-md border border-input bg-background"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">Campanha</th>
                <th className="px-3 py-2 font-medium">Criativo</th>
                <th className="px-3 py-2 font-medium text-right">Spend</th>
                <th className="px-3 py-2 font-medium text-right">Contatos</th>
                <th className="px-3 py-2 font-medium text-right">Pedidos</th>
                <th className="px-3 py-2 font-medium text-right">Receita</th>
                <th className="px-3 py-2 font-medium text-right">ROAS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {filteredCreatives.slice(0, 30).map((c) => (
                <CreativeRow key={`${c.campaign}-${c.creative}`} c={c} />
              ))}
              {filteredCreatives.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                    Nenhum criativo encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {filteredCreatives.length > 30 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
              Mostrando 30 de {filteredCreatives.length} criativos
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground border-t border-border pt-4">
        <strong>Attribution model:</strong> Last-touch UTM (mais simples; multi-touch via Markov virá com Fase 4).{' '}
        <strong>Ground truth:</strong> receita = Ticto (regra absoluta R2 / Princípio #2).
      </div>
    </div>
  );
}
